import { Flex } from "@chakra-ui/react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { usePapaParse } from "react-papaparse";
import { graphql, useFragment } from "react-relay";

import { GraphQLEnums } from "~/src/__generated__/GraphQLEnums";
import type { GenderReportTable_site$key } from "~/src/__relay_artifacts__/GenderReportTable_site.graphql";
import { Table } from "~/src/components/common/tables/Table";

import { CSVDownloadLink } from "../CSVDownloadLink";
import { DisableColumnSelectButton } from "../DisableColumnSelectButton";
import {
  ColumnData,
  ReportTableDataRow,
  ReportTableHeaderRow,
} from "../ReportTable";
import { RowHeightSettingButton } from "../RowHeightSettingButton";
import { disableColumnOptions, headerOptionsForGenderReport } from "../utils";

type Props = {
  onSortClick: (params: any) => void;
  siteRef: GenderReportTable_site$key;
};

const fragment = graphql`
  fragment GenderReportTable_site on Site {
    reportGender(input: $input)
  }
`;

export const GenderReportTable: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ siteRef, onSortClick }) => {
  const { reportGender } = useFragment<typeof siteRef>(fragment, siteRef);

  const [disableColumns, setDisableColumns] = useState<number[]>([]);
  const [rowHeight, setRowHeight] = useState<number>(56);

  const { readString } = usePapaParse();

  const [data, setData] = useState<{ headers: string[]; rows: string[][] }>({
    headers: [],
    rows: [],
  });

  useEffect(() => {
    readString(reportGender.summary_report, {
      worker: true,
      complete: (results: any) => {
        const [headers, ...rest] = results.data;
        setData({ headers, rows: rest });
      },
    });
  }, [readString, reportGender.summary_report]);

  const handleSubmitDisalbeColumnSelect = useCallback((values: any) => {
    setDisableColumns(values.disableColumns);
  }, []);

  const handleSubmitRowHeightSetting = useCallback((values: any) => {
    setRowHeight(values.rowHeight);
  }, []);

  // A map for efficient lookup of gender labels from values, including legacy tokens.
  const genderLabelMap = useMemo(() => {
    const map = new Map<string, string>(
      GraphQLEnums.Gender.map((g) => [g.value, g.label] as const)
    );

    // Add mappings for legacy tokens by looking up the label for their canonical value.
    const maleLabel = map.get("MALE");
    if (maleLabel) map.set("YuOvZzOtrag", maleLabel);
    const femaleLabel = map.get("FEMALE");
    if (femaleLabel) map.set("MSjKrRC4FTY", femaleLabel);

    return map;
  }, []);

  const genderLabelFromValue = useCallback(
    (value: string): string => {
      return genderLabelMap.get(value) ?? value;
    },
    [genderLabelMap]
  );

  const tableRows = useMemo(() => {
    return data.rows.map((row) => {
      return row.map((column) => {
        return {
          type: "TEXT",
          value: genderLabelFromValue(column),
        } as ColumnData;
      });
    });
  }, [data, genderLabelFromValue]);

  const tableFooterRow = useMemo(() => {
    return [
      { type: "TEXT", value: "合計" },
      {
        type: "TEXT",
        value: reportGender.aggregated_report.unique_user as string,
      },
      {
        type: "TEXT",
        value: reportGender.aggregated_report.conversion_user as string,
      },
      {
        type: "TEXT",
        value: reportGender.aggregated_report.conversion_user_rate as string,
      },
      {
        type: "TEXT",
        value: reportGender.aggregated_report.lp_drop_rate as string,
      },
      {
        type: "TEXT",
        value: reportGender.aggregated_report.lp_scroll_rate as string,
      },
      {
        type: "TEXT",
        value: reportGender.aggregated_report.lp_average_stay_time as string,
      },
      {
        type: "TEXT",
        value: reportGender.aggregated_report.form_drop_rate as string,
      },
      { type: "TEXT", value: reportGender.aggregated_report.mobile as string },
      { type: "TEXT", value: reportGender.aggregated_report.tablet as string },
      { type: "TEXT", value: reportGender.aggregated_report.pc as string },
    ] as ColumnData[];
  }, [reportGender]);

  const csvFooterRow = useMemo(() => {
    return [
      "合計",
      reportGender.aggregated_report.unique_user.replaceAll(",", ""),
      reportGender.aggregated_report.conversion_user.replaceAll(",", ""),
      reportGender.aggregated_report.conversion_user_rate,
      reportGender.aggregated_report.lp_drop_rate,
      reportGender.aggregated_report.lp_scroll_rate,
      reportGender.aggregated_report.lp_average_stay_time,
      reportGender.aggregated_report.form_drop_rate,
      reportGender.aggregated_report.mobile,
      reportGender.aggregated_report.tablet,
      reportGender.aggregated_report.pc,
    ].join(",");
  }, [reportGender]);

  return (
    <>
      <Flex justifyContent="flex-end" mb="24px" mt={6}>
        <RowHeightSettingButton
          formValues={{ rowHeight }}
          onSubmit={handleSubmitRowHeightSetting}
        />
        <DisableColumnSelectButton
          formValues={{ disableColumns }}
          onSubmit={handleSubmitDisalbeColumnSelect}
          options={disableColumnOptions}
        />
        <CSVDownloadLink
          name={"性別レポート"}
          data={reportGender.summary_report + csvFooterRow}
          filteredColumns={disableColumns}
        />
      </Flex>
      <Table>
        <ReportTableHeaderRow
          disableColumns={disableColumns}
          onSortClick={onSortClick}
          headerOptions={headerOptionsForGenderReport}
        />
        {tableRows.map((row, i) => (
          <ReportTableDataRow
            key={i}
            row={row}
            disableColumns={disableColumns}
          />
        ))}
        <ReportTableDataRow
          disableColumns={disableColumns}
          row={tableFooterRow}
          rowHeight={`${rowHeight}px`}
        />
      </Table>
    </>
  );
};
